import React from 'react';
import SolutionPageTemplate from './SolutionPageTemplate';

const TEST_GRAPH_CODE = `// What 'zibby test' actually runs under the hood.
// Same primitives you'd use to build any custom pipeline.
import { WorkflowGraph, z } from '@zibby/agent-workflow';

const Assertions    = z.object({ baseUrl: z.string().url(), assertions: z.array(z.string()) });
const BrowserResult = z.object({ passed: z.boolean(), videoPath: z.string().optional() });
const Playwright    = z.object({ scriptPath: z.string(), tests: z.number() });

const graph = new WorkflowGraph();

graph
  .addNode('preflight',       { agent: 'claude', outputSchema: Assertions    })
  .addNode('execute_live',    { agent: 'claude', outputSchema: BrowserResult,
                                skills: ['browser'] })
  .addNode('generate_script', { agent: 'codex',  outputSchema: Playwright    });

graph.addEdge('preflight',    'execute_live');
graph.addEdge('execute_live', 'generate_script');
graph.setEntryPoint('preflight');

export default graph;`;

const TEST_INVOKE_CODE = `# Inline plain-English spec
zibby test "Go to example.com, verify the title is 'Example Domain'"

# Or from a spec file
zibby test test-specs/checkout.txt --agent claude

# Re-run a single node from a past session
zibby test --session 1768974629717 --node generate_script

# CI/CD
- name: Zibby browser test
  env: { ZIBBY_USER_TOKEN: \${{ secrets.ZIBBY_USER_TOKEN }} }
  run: npx @zibby/cli test test-specs/checkout.txt --headless`;

const BrowserTestingPage = () => {
  return (
    <SolutionPageTemplate
      meta={{
        title: 'Browser testing — Zibby',
        description: 'Plain-English test specs → live browser execution → reusable Playwright scripts. Multi-vendor coding-agent recipe with verification video.',
      }}
      eyebrow="RECIPE"
      title="Browser testing, written in English."
      lede="Drive a real browser with a coding agent. Get a reusable Playwright script and a verification video. Mix Claude / Codex / Gemini per run — pick the agent that gives you the best results for your suite."
      ctaPrimary={{ label: 'Try the recipe →', href: 'https://docs.zibby.app/recipes/test', external: true }}
      ctaSecondary={{ label: 'See it in the demo', href: '/connect' }}
      sections={[
        {
          title: 'A 3-node agent that ships in the box.',
          body: (
            <>
              <p>
                <code style={{ background: 'rgba(255,255,255,0.06)', padding: '2px 6px', borderRadius: 4, fontFamily: 'SF Mono, Menlo, monospace', fontSize: '13px' }}>zibby test</code> is a real Zibby agent — preflight extracts assertions, execute_live drives the browser via MCP, generate_script produces a reusable Playwright file. Every node has a Zod schema, every output is validated.
              </p>
              <p>
                Want to fork it? Run <code>zibby workflow new my-test-pipeline</code> and copy the structure. The recipe is the proof of concept; the platform is what you build on.
              </p>
            </>
          ),
          code: TEST_GRAPH_CODE,
        },
        {
          title: 'Run from CLI, CI/CD, or anywhere with HTTP.',
          body: (
            <>
              <p>
                Inline specs, spec files, agent override per run, single-node replay from past sessions, and full CI/CD integration via <code>npx @zibby/cli test</code>.
              </p>
              <p>
                Each run lands in <code>.zibby/output/sessions/&lt;id&gt;/</code> with raw output, parsed JSON, JSONL events, and the recorded video. Replay any node with <code>--session</code> + <code>--node</code> — debug without re-paying for the LLM cost of earlier nodes.
              </p>
            </>
          ),
          code: TEST_INVOKE_CODE,
        },
        {
          title: 'Studio for live test runs.',
          body: (
            <>
              <p>
                <a href="https://zibby.app/connect">Zibby Studio</a> is a desktop UI optimised for the test recipe today. Visualise live runs, scrub past sessions, stop an agent with one click. Broader agent support is on the roadmap.
              </p>
              <p>
                For non-test agents, use the CLI's <code>--session</code> + <code>--node</code> flags to replay any node directly.
              </p>
            </>
          ),
        },
        {
          title: 'How it compares.',
          body: (
            <>
              <table className="sp-table">
                <thead>
                  <tr><th></th><th>Playwright codegen</th><th>LLM-only script</th><th>Zibby test recipe</th></tr>
                </thead>
                <tbody>
                  <tr><td>Plain-English input</td><td className="sp-no">—</td><td className="sp-yes">✓</td><td className="sp-yes">✓</td></tr>
                  <tr><td>Real browser execution</td><td className="sp-yes">✓</td><td className="sp-no">just generates code</td><td className="sp-yes">✓</td></tr>
                  <tr><td>Coding-agent driven</td><td className="sp-no">—</td><td className="sp-no">partial</td><td className="sp-yes">✓ Claude / Codex / Gemini</td></tr>
                  <tr><td>Multi-step verification</td><td className="sp-no">—</td><td className="sp-no">—</td><td className="sp-yes">✓ Zod-validated nodes</td></tr>
                  <tr><td>Replayable + debuggable</td><td className="sp-no">—</td><td className="sp-no">—</td><td className="sp-yes">✓ Studio + CLI replay</td></tr>
                  <tr><td>Vendor-neutral</td><td>N/A</td><td className="sp-no">locked to one LLM</td><td className="sp-yes">✓ swap agent per run</td></tr>
                </tbody>
              </table>
            </>
          ),
        },
      ]}
      cta={{
        title: 'Run a browser test in 60 seconds.',
        body: 'No project setup needed — point it at a URL and a plain-English spec.',
        primary: { label: 'Open the recipe docs →', href: 'https://docs.zibby.app/recipes/test', external: true },
        secondary: { label: 'Try the demo', href: '/connect' },
      }}
    />
  );
};

export default BrowserTestingPage;
